import { IntermediateReportInterval, IntermediateReportIntervalValue, REPORT_INTERVAL, ScheduledIntermediateReport } from '@abovo/shared-models';
import { DateInputComponent, DropdownInputComponent, SimpleDialogContentInstance, SimpleDialogData } from '@abovo/shared-ui-components';
import { DateFnsUtilityService } from '@abovo/shared-utils';
import { IntermediateScheduledReportService } from '@abovo/webvis-data-access';
import { Component, computed, Inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { add, Day, isSameDay, startOfToday } from 'date-fns';
import { catchError, combineLatest, debounceTime, filter, finalize, map, of, skip, startWith, switchMap, tap } from 'rxjs';

export interface ReportSettingsDialogData {
   reportInterval?: IntermediateReportInterval | null;
   scheduledReports: ScheduledIntermediateReport[];
   campaignDates: { start: Date; end: Date };
}

@UntilDestroy()
@Component({
   templateUrl: './report-settings-dialog.component.html',
   imports: [DateInputComponent, MatIconButton, FaIconComponent, MatButton, TranslatePipe, DropdownInputComponent, MatTooltip, MatProgressSpinner],
})
export class ReportSettingsDialogComponent implements SimpleDialogContentInstance, OnInit {
   readonly today = startOfToday();
   reportDateMin = this.data.dialogData.campaignDates.start > this.today ? this.data.dialogData.campaignDates.start : DateFnsUtilityService.getTomorrow(true);
   reportDateMax = add(this.data.dialogData.campaignDates.end, { weeks: 2 });
   readonly intervalFormData = [null, ...REPORT_INTERVAL].map((interval) => ({
      value: interval,
      label: this.getIntervalTranslation(interval),
   })) as { value: IntermediateReportIntervalValue; label: string }[];

   /** Disable saturday and sunday in datepicker */
   readonly disabledReportDays: Day[] = [0, 6];
   private readonly campaignEnded = this.reportDateMax < this.today;

   fg = this.fb.group({
      reportDate: [
         {
            value: this.data.dialogData.reportInterval?.report_date || this.reportDateMin,
            disabled: this.campaignEnded,
         },
         Validators.required,
      ],
      interval: [
         {
            value: {
               value: this.data.dialogData.reportInterval?.interval || null,
               label: this.getIntervalTranslation(this.data.dialogData.reportInterval?.interval),
            },
            disabled: this.campaignEnded,
         },
         Validators.required,
      ],
      reports: this.fb.array(
         this.data.dialogData.scheduledReports
            .sort((a, b) => a.scheduled_report_date.getTime() - b.scheduled_report_date.getTime())
            .map((scheduledReport) => this.createFormControl(scheduledReport)) ?? [],
      ),
   });

   private intervalValueChanges$ = this.fg.controls.interval.valueChanges.pipe(
      filter((val) => !!val),
      map((val) => val.value),
   );
   currentInterval = toSignal(this.intervalValueChanges$, { initialValue: this.fg.controls.interval.value.value });

   /** True while the generated report dates are being fetched from the backend. */
   loadingDates = signal(false);

   canAddDate = computed(() => {
      const activeCampaign = this.data.dialogData.campaignDates.end > this.today;
      const hasInterval = this.currentInterval() !== null;
      const intervalRangeIncomplete = this.currentInterval() !== 'one_off';
      return activeCampaign && hasInterval && intervalRangeIncomplete;
   });

   constructor(
      private fb: NonNullableFormBuilder,
      private translate: TranslateService,
      private scheduledReportService: IntermediateScheduledReportService,
      // The opener always passes the dialog data
      @Inject(MAT_DIALOG_DATA) public data: SimpleDialogData<ReportSettingsDialogData> & { dialogData: ReportSettingsDialogData },
   ) {}

   ngOnInit() {
      this.wireUpReportControlsToInterval().subscribe();
   }

   /**
    * Keeps the report-row list in sync with the interval + anchor: pulls the generator's dates
    * from the backend preview endpoint and rebuilds the form array, preserving manual rows.
    */
   private wireUpReportControlsToInterval() {
      return combineLatest([
         this.intervalValueChanges$.pipe(startWith(this.fg.controls.interval.value.value)),
         this.fg.controls.reportDate.valueChanges.pipe(
            startWith(this.fg.controls.reportDate.value),
            filter(() => this.fg.controls.reportDate.valid),
         ),
      ]).pipe(
         untilDestroyed(this),
         // First emit needs to be skipped to prevent overriding default values and potentially adding unwanted controls.
         skip(1),
         // Tabbing through the dropdown / date picker can fire several changes in a row;
         // wait briefly before hitting the preview endpoint so we don't burn a request per keystroke.
         debounceTime(150),
         tap(([interval]) => this.loadingDates.set(interval !== null)),
         switchMap(([interval, reportDate]) => this.fetchPreviewDates(interval, reportDate)),
         tap(({ interval, dates }) => this.rebuildReportControls(interval, dates)),
      );
   }

   /**
    * Asks the backend for the dates it would generate, so what the dialog shows (and saves)
    * matches the backend generator exactly — moving a campaign date later won't reshuffle them.
    */
   private fetchPreviewDates(interval: IntermediateReportIntervalValue | null, reportDate: Date) {
      if (interval === null) {
         return of<{ interval: IntermediateReportIntervalValue | null; dates: Date[] }>({ interval, dates: [] });
      }
      return this.scheduledReportService.previewDates(interval, reportDate, this.data.dialogData.campaignDates.end).pipe(
         map((dates) => ({ interval, dates })),
         catchError(() => of({ interval, dates: [] as Date[] })),
         finalize(() => this.loadingDates.set(false)),
      );
   }

   private rebuildReportControls(interval: IntermediateReportIntervalValue | null, dates: Date[]) {
      const manualSetControls = this.fg.controls.reports.controls.filter((control) => control.value._metadata?.is_manual_set || control.dirty);
      if (interval === null) {
         // Keep manual rows when clearing the interval — they're sticky and must survive
         // the backend's diff (the exhaustive-list save would otherwise delete them).
         this.overwriteReportControls(manualSetControls);
         return;
      }

      const generatedControls = dates.map((date) => {
         const existingControl = this.fg.controls.reports.controls.find((control) => isSameDay(date, control.getRawValue().date));
         return existingControl || this.createFormControl({ scheduled_report_date: date });
      });
      const sortedControls = [...manualSetControls, ...generatedControls].sort((a, b) => a.getRawValue().date.getTime() - b.getRawValue().date.getTime());

      this.overwriteReportControls(sortedControls);
   }

   public customSaveValue() {
      const intervalValue = this.fg.controls.interval.value.value;
      // Exhaustive list of rows: backend soft-deletes anything in DB but not here. Each row's
      // id (when present) preserves it, absence creates a new row.
      const reports = this.fg.controls.reports.controls.map((control) => {
         const value = control.getRawValue();
         const item = {
            scheduled_report_date: value.date,
            is_manual_set: control.dirty || value._metadata?.is_manual_set || false,
         };
         // An id marks an existing row to update; its absence creates a new one.
         const id = value._metadata?.id;
         return id !== undefined ? { ...item, id } : item;
      });

      return {
         // A value sets the interval, null removes the schedule.
         interval: intervalValue,
         reportDate: this.fg.controls.reportDate.value,
         reports,
      };
   }

   addReportDate() {
      const formGroup = this.createFormControl();
      formGroup.controls.date.markAsDirty();
      this.fg.controls.reports.push(formGroup);
   }

   removeReportDate(index: number) {
      this.fg.controls.reports.removeAt(index);
   }

   private createFormControl(scheduledReport?: Partial<ScheduledIntermediateReport> & { scheduled_report_date: Date }) {
      // Default to reportDateMin so a manually added row starts inside the datepicker's
      // [min, max] range. Falling back to plain "tomorrow" silently fails for campaigns that
      // haven't started yet — the datepicker rejects the value with no visible message.
      const scheduledReportDate = scheduledReport?.scheduled_report_date || this.reportDateMin;
      return this.fb.group({
         date: [{ value: scheduledReportDate, disabled: scheduledReportDate <= this.today || false }, Validators.required],
         _metadata: scheduledReport?.id ? scheduledReport : null,
      });
   }

   private getIntervalTranslation(interval?: IntermediateReportIntervalValue | null): string {
      return this.translate.instant(interval ? 'intermediate_report.interval.' + interval : 'intermediate_report.interval.none');
   }

   private overwriteReportControls(controls: ReturnType<typeof this.createFormControl>[]) {
      this.fg.controls.reports.clear();
      controls.forEach((control) => {
         this.fg.controls.reports.push(control, { emitEvent: false });
      });
   }
}
